/**
 * Plugin Modulaire : Lecteur Audio/Playlist Avancé
 * Wrapper TypeScript pour intégration dans le système modulaire
 */

import React, { useState, useRef, useEffect } from 'react'; // ✅ Importer useEffect
import type { PluginInterface, PluginMetadata, PluginConfigProps, PluginPreviewProps } from '../types/PluginInterface';
import { generateUniformPlaceholder } from '../utils/PlaceholderGenerator';
// ✅ AJOUT : Importer le hook pour récupérer les clés API
import { usePaymentApiKeys } from '../hooks/usePaymentApiKeys'; 
import AutoTranslateText from '../components/AutoTranslateText'; // ✅ import

interface AudioTrack {
  title: string;
  artist: string;
  url: string;
  duration?: number;
  type?: 'audio' | 'youtube' | 'vimeo';
  videoId?: string;
  thumbnail?: string;
}

interface AudioPlayerConfig {
  title?: string;
  width?: string;
  height?: string;
  theme?: 'dark' | 'light';
  showWaveform?: boolean;
  showPlaylist?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  shuffle?: boolean;
  volume?: number;
  visualEffects?: boolean;
  enableKeyboardShortcuts?: boolean;
  playlist?: AudioTrack[];
  backgroundColor?: string;
  primaryColor?: string;
  secondaryColor?: string;
  enableAIPlaylistGeneration?: boolean;
  openAIApiKey?: string;
  // ✅ AJOUT
  aiTrackCount?: number;      // nombre de morceaux à générer
  aiTotalMinutes?: number;    // durée totale d’écoute (minutes)
  aiModel?: string;
}

// Configuration par défaut du plugin audio
const defaultAudioPlayerConfig: AudioPlayerConfig = {
  title: 'Lecteur Audio',
  width: '100%',
  height: '400px',
  theme: 'dark',
  showWaveform: true,
  showPlaylist: true,
  autoplay: false,
  loop: false,
  shuffle: false,
  volume: 0.8,
  visualEffects: true,
  enableKeyboardShortcuts: true,
  playlist: [
    {
      title: 'Fichier Audio de Test 1',
      artist: 'Artiste Test',
      url: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.wav'
    },
    {
      title: 'Fichier Audio de Test 2',
      artist: 'Démo Audio', 
      url: 'https://file-examples.com/storage/fe86c86c707c8bd0c70962b/2017/11/file_example_MP3_700KB.mp3'
    },
    {
      title: 'Exemple Musique Libre',
      artist: 'Creative Commons',
      url: 'https://www.learningcontainer.com/wp-content/uploads/2020/02/Kalimba.mp3'
    }
  ],
  backgroundColor: '#1a1a1a',
  primaryColor: '#007bff',
  secondaryColor: '#6c757d'
};

// Composant de configuration du lecteur audio
const AudioPlayerConfigComponent: React.FC<PluginConfigProps> = ({ 
  config, 
  onConfigChange, 
  t 
}) => {
  const audioConfig = config as AudioPlayerConfig;

  // ✅ Récupérer uniquement les propriétés existantes du hook
  const { hasOpenAIKey, openAIApiKey: profileOpenAIApiKey } = usePaymentApiKeys();

  // ✅ Fallbacks locaux (le hook ne fournit pas ces états)
  const keysLoading = false;
  const keysError: string | null = null;

  // ✅ Choisir la clé à utiliser: priorité au profil, sinon à la config
  const openAIApiKey = (profileOpenAIApiKey || audioConfig.openAIApiKey || '').trim();

  const [newTrack, setNewTrack] = React.useState<AudioTrack>({ title: '', artist: '', url: '' });
  const [showAddTrack, setShowAddTrack] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<'general' | 'playlist' | 'appearance'>('general');

  const handleInputChange = (field: keyof AudioPlayerConfig, value: any) => {
    onConfigChange({ ...audioConfig, [field]: value });
  };

  const addTrackToPlaylist = () => {
    if (newTrack.title && newTrack.url) {
      const updatedPlaylist = [...(audioConfig.playlist || []), { ...newTrack }];
      handleInputChange('playlist', updatedPlaylist);
      setNewTrack({ title: '', artist: '', url: '' });
      setShowAddTrack(false);
    }
  };

  const removeTrackFromPlaylist = (index: number) => {
    const updatedPlaylist = (audioConfig.playlist || []).filter((_, i) => i !== index);
    handleInputChange('playlist', updatedPlaylist);
  };

  const extractYouTubeId = (url: string) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  const extractVimeoId = (url: string) => {
    const regExp = /(?:vimeo)\.com.*(?:videos|video|channels|)\/([\d]+)/i;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  const detectMediaType = (url: string): { type: 'audio' | 'youtube' | 'vimeo', videoId?: string } => {
    const youtubeId = extractYouTubeId(url);
    if (youtubeId) {
      return { type: 'youtube', videoId: youtubeId };
    }
    
    const vimeoId = extractVimeoId(url);
    if (vimeoId) {
      return { type: 'vimeo', videoId: vimeoId };
    }
    
    return { type: 'audio' };
  };

  const generateAIPlaylist = async (prompt: string) => {
    if (!openAIApiKey || !openAIApiKey.trim()) {
      alert('Veuillez configurer votre clé API OpenAI.');
      return;
    }

    const selectedModel = (audioConfig.aiModel || '').trim() || 'gpt-4o-mini';

    try {
      const response = await fetch('http://localhost:3000/api/ai-playlist/generate-playlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          openAIApiKey,
          trackCount: audioConfig.aiTrackCount || undefined,
          totalMinutes: audioConfig.aiTotalMinutes || undefined,
          model: selectedModel,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        let msg = `Erreur HTTP ${response.status}`;
        try {
          const err = JSON.parse(text);
          msg = err.error || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await response.json();
      const generatedPlaylist: AudioTrack[] = Array.isArray(data?.playlist) ? data.playlist : [];
      if (generatedPlaylist.length === 0) {
        throw new Error('La réponse IA ne contient aucune piste.');
      }

      // Filtrer les pistes invalides
      const sanitized = generatedPlaylist.filter(t => t && t.title && t.url);

      // Ajouter à la playlist existante
      const currentPlaylist = audioConfig.playlist || [];
      const updatedPlaylist = [...currentPlaylist, ...sanitized];
      handleInputChange('playlist', updatedPlaylist);

      const keywords = data?.analysis?.keywords;
      const kwText = Array.isArray(keywords) && keywords.length ? `\nGenres détectés: ${keywords.join(', ')}` : '';
      alert(`Playlist générée avec succès ! ${sanitized.length} pistes ajoutées.${kwText}`);
    } catch (error: any) {
      console.error('Erreur génération playlist:', error);
      alert(error?.message || 'Erreur lors de la génération de la playlist.');
    }
  };

  const handleUrlChange = (url: string) => {
    const mediaInfo = detectMediaType(url);
    setNewTrack(prev => {
      const next = {
        ...prev,
        url,
        type: mediaInfo.type,
        videoId: mediaInfo.videoId
      };
      // Définir un titre par défaut seulement si absent
      if (!prev.title) {
        if (mediaInfo.type === 'youtube' && mediaInfo.videoId) {
          next.title = `Vidéo YouTube ${mediaInfo.videoId}`;
        } else if (mediaInfo.type === 'vimeo' && mediaInfo.videoId) {
          next.title = `Vidéo Vimeo ${mediaInfo.videoId}`;
        }
      }
      return next;
    });
  };

  // ✅ Helper traduction pour attributs (placeholders/titles/alt)
  const tr = (s: string) => (typeof t === 'function' ? t(s) : s);

  return (
    <div className="audio-player-config">
      {/* Navigation par onglets */}
      <div style={{ borderBottom: '1px solid #dee2e6', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '0' }}>
          {{
            general: { label: 'Général', icon: '⚙️' },
            playlist: { label: 'Playlist', icon: '🎵' },
            appearance: { label: 'Apparence', icon: '🎨' }
          }[activeTab] && Object.entries({
            general: { label: 'Général', icon: '⚙️' },
            playlist: { label: 'Playlist', icon: '🎵' },
            appearance: { label: 'Apparence', icon: '🎨' }
          }).map(([key, tab]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderBottom: activeTab === key ? '2px solid #007bff' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: activeTab === key ? '#007bff' : '#6c757d',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === key ? '500' : 'normal'
              }}
            >
              {tab.icon} <AutoTranslateText sourceLang="fr">{tab.label}</AutoTranslateText>
            </button>
          ))}
        </div>
      </div>

      {/* Onglet Général */}
      {activeTab === 'general' && (
        <div>
          <div style={{ marginBottom: '16px' }}>
            <h6 style={{ fontSize: '14px', marginBottom: '8px', color: '#6c757d' }}>
              <AutoTranslateText sourceLang="fr">Dimensions</AutoTranslateText>
            </h6>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px' }}>Largeur</label>
                <input
                  type="text"
                  value={audioConfig.width || ''}
                  onChange={(e) => handleInputChange('width', e.target.value)}
                  placeholder={tr('ex: 100%, 500px')}
                  title={tr('Largeur du lecteur')}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px' }}>Hauteur</label>
                <input
                  type="text"
                  value={audioConfig.height || ''}
                  onChange={(e) => handleInputChange('height', e.target.value)}
                  placeholder={tr('ex: 400px')}
                  title={tr('Hauteur du lecteur')}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                />
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <h6 style={{ fontSize: '14px', marginBottom: '8px', color: '#6c757d' }}>
              <AutoTranslateText sourceLang="fr">Fonctionnalités</AutoTranslateText>
            </h6>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={audioConfig.showWaveform || false}
                  onChange={(e) => handleInputChange('showWaveform', e.target.checked)}
                />
                <AutoTranslateText sourceLang="fr">Afficher la forme d'onde</AutoTranslateText>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={audioConfig.showPlaylist || false}
                  onChange={(e) => handleInputChange('showPlaylist', e.target.checked)}
                />
                <AutoTranslateText sourceLang="fr">Afficher la playlist</AutoTranslateText>
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={audioConfig.autoplay || false}
                  onChange={(e) => handleInputChange('autoplay', e.target.checked)}
                />
                <AutoTranslateText sourceLang="fr">Lecture automatique</AutoTranslateText>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={audioConfig.loop || false}
                  onChange={(e) => handleInputChange('loop', e.target.checked)}
                />
                <AutoTranslateText sourceLang="fr">Lecture en boucle</AutoTranslateText>
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={audioConfig.shuffle || false}
                  onChange={(e) => handleInputChange('shuffle', e.target.checked)}
                />
                <AutoTranslateText sourceLang="fr">Lecture aléatoire</AutoTranslateText>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={audioConfig.visualEffects || false}
                  onChange={(e) => handleInputChange('visualEffects', e.target.checked)}
                />
                <AutoTranslateText sourceLang="fr">Effets visuels</AutoTranslateText>
              </label>
            </div>

            <div style={{ marginTop: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={audioConfig.enableAIPlaylistGeneration || false}
                  onChange={(e) => handleInputChange('enableAIPlaylistGeneration', e.target.checked)}
                />
                <span>🤖 <AutoTranslateText sourceLang="fr">Activer la génération de playlist par IA</AutoTranslateText></span>
              </label>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px' }}>
              <AutoTranslateText sourceLang="fr">Volume par défaut</AutoTranslateText>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={audioConfig.volume || 0.8}
                onChange={(e) => handleInputChange('volume', parseFloat(e.target.value))}
                style={{ flex: 1 }}
              />
              <span style={{ minWidth: '40px', fontSize: '14px' }}>
                {Math.round((audioConfig.volume || 0.8) * 100)}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Onglet Playlist */}
      {activeTab === 'playlist' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h6 style={{ fontSize: '16px', margin: 0 }}>Gestion de la playlist</h6>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowAddTrack(true)
                }
                title={tr('Ajouter une piste')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                <AutoTranslateText sourceLang="fr">➕ Ajouter une piste</AutoTranslateText>
              </button>
              {audioConfig.enableAIPlaylistGeneration && (
                <button
                  onClick={() => {
                    if (!hasOpenAIKey) {
                      alert(tr("La génération par IA est activée, mais aucune clé API OpenAI n'a été trouvée dans votre profil."));
                      return;
                    }
                    const prompt = window.prompt(tr('Décrivez la playlist que vous souhaitez générer (ex: "musique des années 80", "playlist de Mozart", "hits d\'été 2020")'));
                    if (prompt) {
                      generateAIPlaylist(prompt);
                    }
                  }}
                  disabled={!hasOpenAIKey || keysLoading}
                  title={tr('Générer automatiquement une playlist avec l’IA')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: !hasOpenAIKey || keysLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: !hasOpenAIKey || keysLoading ? 0.6 : 1
                  }}
                >
                  <AutoTranslateText sourceLang="fr">🤖 Générer avec IA</AutoTranslateText>
                </button>
              )}
            </div>
          </div>

          {/* Configuration IA */}
          {audioConfig.enableAIPlaylistGeneration && (
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '16px', 
              borderRadius: '8px', 
              marginBottom: '16px',
              border: '1px solid #dee2e6'
            }}>
              <h6 style={{ margin: '0 0 12px 0' }}>
                <AutoTranslateText sourceLang="fr">Configuration IA</AutoTranslateText>
              </h6>
              
              {/* ❌ SUPPRIMÉ : Le champ de saisie de la clé API est retiré */}

              {/* ✅ AJOUT : Afficher un message sur l'état de la clé API */}
              {keysLoading && (
                <div className="alert alert-info small p-2">
                  <AutoTranslateText sourceLang="fr">Chargement de la clé API...</AutoTranslateText>
                </div>
              )}
              {keysError && (
                <div className="alert alert-danger small p-2">
                  <AutoTranslateText sourceLang="fr">Erreur lors du chargement des clés API.</AutoTranslateText>
                </div>
              )}
              {!keysLoading && !hasOpenAIKey && (
                <div className="alert alert-warning small p-2">
                  <AutoTranslateText sourceLang="fr">
                    Aucune clé API OpenAI n'est configurée. Veuillez l'ajouter dans votre Profil &gt; Clés API pour utiliser cette fonctionnalité.
                  </AutoTranslateText>
                </div>
              )}
              {!keysLoading && hasOpenAIKey && (
                <div className="alert alert-success small p-2">
                  <AutoTranslateText sourceLang="fr">✅ Clé API OpenAI détectée et prête à l'emploi.</AutoTranslateText>
                </div>
              )}

              {/* ✅ AJOUT: Contraintes de génération */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px' }}>
                    <AutoTranslateText sourceLang="fr">Nombre de morceaux</AutoTranslateText>
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={audioConfig.aiTrackCount ?? ''}
                    onChange={(e) => {
                      const v = e.target.value === '' ? undefined : Math.max(1, Math.min(50, Number(e.target.value)));
                      onConfigChange({ ...audioConfig, aiTrackCount: v });
                    }}
                    placeholder={tr('ex: 8')}
                    title={tr('Nombre de morceaux à générer')}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px' }}>
                    <AutoTranslateText sourceLang="fr">Durée totale (minutes)</AutoTranslateText>
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={300}
                    value={audioConfig.aiTotalMinutes ?? ''}
                    onChange={(e) => {
                      const v = e.target.value === '' ? undefined : Math.max(1, Math.min(300, Number(e.target.value)));
                      onConfigChange({ ...audioConfig, aiTotalMinutes: v });
                    }}
                    placeholder={tr('ex: 30')}
                    title={tr('Durée totale d’écoute souhaitée (minutes)')}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                  />
                </div>
              </div>
              <small style={{ display: 'block', marginTop: '8px', color: '#6c757d' }}>
                <AutoTranslateText sourceLang="fr">
                  Renseignez l’un des deux. Si les deux sont fournis, la durée totale est prioritaire.
                </AutoTranslateText>
              </small>

              {/* ✅ Sélecteur de modèle */}
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>
                  <AutoTranslateText sourceLang="fr">Modèle IA</AutoTranslateText>
                </label>
                <select
                  value={audioConfig.aiModel || 'gpt-4o-mini'}
                  onChange={(e) => onConfigChange({ ...audioConfig, aiModel: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                >
                  <option value="gpt-4o-mini">gpt-4o-mini (recommandé)</option>
                  <option value="gpt-4o">gpt-4o</option>
                  {/* Vous pouvez ajouter d’autres modèles pris en charge par votre clé/billing */}
                  <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                  <option value="gpt-4.1">gpt-4.1</option>
                  {/* Accepte aussi une valeur libre si vous stockez un autre modèle */}
                </select>
                <small style={{ color: '#6c757d' }}>
                  <AutoTranslateText sourceLang="fr">Utilisez un modèle supporté par votre plan OpenAI.</AutoTranslateText>
                </small>
              </div>

              <div style={{ 
                backgroundColor: '#e7f3ff', 
                padding: '12px', 
                borderRadius: '4px',
                border: '1px solid #b3d9ff',
                marginTop: '12px'
              }}>
                <strong style={{ color: '#004085' }}>
                  <AutoTranslateText sourceLang="fr">💡 Exemples de prompts :</AutoTranslateText>
                </strong>
                <ul style={{ margin: '8px 0 0 0', color: '#004085' }}>
                  <li><AutoTranslateText sourceLang="fr">Playlist des années 80 avec du rock et de la pop</AutoTranslateText></li>
                  <li><AutoTranslateText sourceLang="fr">Musique classique de Bach et Mozart</AutoTranslateText></li>
                  <li><AutoTranslateText sourceLang="fr">Hits français des années 2000</AutoTranslateText></li>
                  <li><AutoTranslateText sourceLang="fr">Playlist chill pour travailler</AutoTranslateText></li>
                </ul>
              </div>
            </div>
          )}

          {/* Liste des pistes */}
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
            {(!Array.isArray(audioConfig.playlist) || audioConfig.playlist.length === 0) ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                <AutoTranslateText sourceLang="fr">Aucune piste dans la playlist. Cliquez sur "Ajouter une piste" pour en ajouter.</AutoTranslateText>
              </div>
            ) : (
              audioConfig.playlist.map((track, index) => (
                <div
                  key={index}
                  style={{
                    padding: '12px',
                    borderBottom: index < audioConfig.playlist!.length - 1 ? '1px solid #dee2e6' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', fontSize: '14px', marginBottom: '4px' }}>
                      {track.type === 'youtube' ? '📺' : track.type === 'vimeo' ? '' : '🎵'} {track.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>
                      👤 {track.artist || 'Artiste inconnu'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6c757d', wordBreak: 'break-all' }}>
                      {track.type === 'youtube' ? ' YouTube' : 
                       track.type === 'vimeo' ? '🎬 Vimeo' : 
                       '🎵 Audio'} • {track.url}
                    </div>
                  </div>
                  <button
                    onClick={() => removeTrackFromPlaylist(index)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    🗑️
                  </button>
                </div>
              ))
           )}
          </div>

          {/* Modal d'ajout de piste */}
          {showAddTrack && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '24px',
                width: '400px',
                maxWidth: '90vw'
              }}>
                <h5 style={{ marginBottom: '20px' }}>
                  <AutoTranslateText sourceLang="fr">Ajouter une piste</AutoTranslateText>
                </h5>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    <AutoTranslateText sourceLang="fr">Titre *</AutoTranslateText>
                  </label>
                  <input
                    type="text"
                    value={newTrack.title}
                    onChange={(e) => setNewTrack({ ...newTrack, title: e.target.value })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                    placeholder={tr('Titre de la piste')}
                    title={tr('Titre de la piste')}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    <AutoTranslateText sourceLang="fr">Artiste</AutoTranslateText>
                  </label>
                  <input
                    type="text"
                    value={newTrack.artist}
                    onChange={(e) => setNewTrack({ ...newTrack, artist: e.target.value })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                    placeholder={tr("Nom de l'artiste")}
                    title={tr("Nom de l'artiste")}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    <AutoTranslateText sourceLang="fr">URL *</AutoTranslateText>
                  </label>
                  <input
                    type="url"
                    value={newTrack.url}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                    placeholder={tr('URL du fichier audio, YouTube ou Vimeo')}
                    title={tr('URL du fichier audio, YouTube ou Vimeo')}
                  />
                  <small style={{ color: '#6c757d', fontSize: '12px' }}>
                    <strong><AutoTranslateText sourceLang="fr">Formats supportés :</AutoTranslateText></strong><br/>
                    🎵 Audio : MP3, WAV, OGG, M4A<br/>
                    📺 Vidéo : YouTube, Vimeo<br/>
                    🎶 Streaming : SoundCloud, Spotify (liens publics)
                  </small>
                  
                  {/* Détection automatique du type */}
                  {newTrack.url && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '4px',
                      border: '1px solid #dee2e6'
                    }}>
                      <strong><AutoTranslateText sourceLang="fr">Type détecté :</AutoTranslateText></strong> {
                        newTrack.type === 'youtube' ? '📺 YouTube' :
                        newTrack.type === 'vimeo' ? '📺 Vimeo' :
                        '🎵 Fichier Audio'
                      }
                      {newTrack.videoId && (
                        <span style={{ marginLeft: '8px', color: '#6c757d' }}>
                          (ID: {newTrack.videoId})
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowAddTrack(false);
                      setNewTrack({ title: '', artist: '', url: '' });
                    }}
                    title={tr('Annuler')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    <AutoTranslateText sourceLang="fr">Annuler</AutoTranslateText>
                  </button>
                  <button
                    onClick={addTrackToPlaylist}
                    title={tr('Ajouter')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    <AutoTranslateText sourceLang="fr">Ajouter</AutoTranslateText>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Onglet Apparence */}
      {activeTab === 'appearance' && (
        <div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px' }}>Thème</label>
            <select
              value={audioConfig.theme || 'dark'}
              onChange={(e) => handleInputChange('theme', e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
            >
              <option value="dark">Thème Sombre</option>
              <option value="light">Thème Clair</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <h6 style={{ fontSize: '14px', marginBottom: '8px', color: '#6c757d' }}>Couleurs personnalisées</h6>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px' }}>Arrière-plan</label>
                <input
                  type="color"
                  value={audioConfig.backgroundColor || '#1a1a1a'}
                  onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid #ced4da', borderRadius: '4px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px' }}>Couleur principale</label>
                <input
                  type="color"
                  value={audioConfig.primaryColor || '#007bff'}
                  onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid #ced4da', borderRadius: '4px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px' }}>Couleur secondaire</label>
                <input
                  type="color"
                  value={audioConfig.secondaryColor || '#6c757d'}
                  onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid #ced4da', borderRadius: '4px', cursor: 'pointer' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant de prévisualisation
const AudioPlayerRenderer: React.FC<PluginPreviewProps> = ({ config }) => {
  const audioConfig = config as AudioPlayerConfig;
  const [currentTrackIndex, setCurrentTrackIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [volume, setVolume] = useState<number>(audioConfig.volume || 0.8);
  const audioRef = useRef<HTMLAudioElement>(null);

  // ✅ Helper traduction local (pas de `t` ici)
  const tr = (s: string) => s;

  // Appliquer/Resynchroniser le volume quand la config change
  useEffect(() => {
    setVolume(audioConfig.volume ?? 0.8);
  }, [audioConfig.volume]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Gérer la lecture/pause
  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Piste précédente
  const previousTrack = () => {
    if (audioConfig.playlist && audioConfig.playlist.length > 0) {
      const newIndex = currentTrackIndex > 0 ? currentTrackIndex - 1 : audioConfig.playlist.length - 1;
      setCurrentTrackIndex(newIndex);
    }
  };

  // Piste suivante
  const nextTrack = () => {
    if (audioConfig.playlist && audioConfig.playlist.length > 0) {
      const newIndex = currentTrackIndex < audioConfig.playlist.length - 1 ? currentTrackIndex + 1 : 0;
      setCurrentTrackIndex(newIndex);
    }
  };

  // Sélectionner une piste
  const selectTrack = (index: number) => {
    setCurrentTrackIndex(index);
    if (isPlaying && audioRef.current) {
      audioRef.current.play();
    }
  };

  // Mettre à jour le temps
  const updateTime = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
    }
  };

  // Gérer la fin de piste
  const handleTrackEnd = () => {
    if (audioConfig.loop) {
      audioRef.current?.play();
    } else if (audioConfig.playlist && currentTrackIndex < audioConfig.playlist.length - 1) {
      nextTrack();
    } else {
      setIsPlaying(false);
    }
  };

  // Formater le temps
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentTrack = audioConfig.playlist?.[currentTrackIndex];

  // Composant pour afficher la vidéo/audio
  const MediaPlayer = () => {
    if (!currentTrack) return null;

    if (currentTrack.type === 'youtube') {
      return (
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <iframe
            width="100%"
            src={`https://www.youtube.com/embed/${currentTrack.videoId}?autoplay=${isPlaying ? '1' : '0'}&controls=1`}
            frameBorder="0"
            title={tr('Lecteur YouTube')}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ width: '100%', height: 'auto', aspectRatio: '16 / 9', maxHeight: '50vh', borderRadius: '8px' }}
          />
        </div>
      );
    }

    if (currentTrack.type === 'vimeo') {
      return (
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <iframe
            width="100%"
            src={`https://player.vimeo.com/video/${currentTrack.videoId}?autoplay=${isPlaying ? '1' : '0'}`}
            frameBorder="0"
            title={tr('Lecteur Vimeo')}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            style={{ width: '100%', height: 'auto', aspectRatio: '16 / 9', maxHeight: '50vh', borderRadius: '8px' }}
          />
        </div>
      );
    }

    return (
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <audio
          ref={audioRef}
          src={currentTrack.url}
          onTimeUpdate={updateTime}
          onEnded={handleTrackEnd}
          onLoadedMetadata={updateTime}
          aria-label={tr('Lecteur audio')}
          title={tr('Lecteur audio')}
          style={{ width: '100%', maxWidth: 'min(90vw, 480px)' }}
        />
        <div style={{ textAlign: 'center', color: '#fff' }}>
          <div style={{ fontSize: '18px', marginBottom: '8px' }}>🎵</div>
          <div style={{ fontSize: '14px', opacity: 0.8 }}><AutoTranslateText sourceLang="fr">Lecteur Audio</AutoTranslateText></div>
        </div>
      </div>
    );
  };

  return (
    <div 
      className={`audio-player-container theme-${audioConfig.theme}`}
      style={{
        width: audioConfig.width || '100%',
        maxWidth: '100%',
        height: audioConfig.height || 'auto',
        maxHeight: '90vh',
        backgroundColor: audioConfig.backgroundColor || '#1a1a1a',
        color: audioConfig.theme === 'dark' ? '#fff' : '#333',
        border: '1px solid #ddd',
        borderRadius: '8px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      {/* Header */}
      <div 
        style={{
          padding: '12px',
          borderBottom: '1px solid #333',
          textAlign: 'center',
          backgroundColor: 'rgba(0,0,0,0.2)'
        }}
      >
        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '4px' }}>
          {currentTrack?.type === 'youtube' ? '📺 YouTube' : 
           currentTrack?.type === 'vimeo' ? '🎬 Vimeo' : 
           '🎵 Audio Player'}
        </div>
        <div style={{ fontSize: '14px', opacity: 0.8 }}>
          {currentTrack?.title || 'Aucune piste sélectionnée'}
        </div>
        <div style={{ fontSize: '12px', opacity: 0.6 }}>
          {currentTrack?.artist || ''}
        </div>
      </div>

      {/* Lecteur principal */}
      <MediaPlayer />

      {/* Contrôles */}
      <div 
        style={{
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          borderBottom: audioConfig.showPlaylist ? '1px solid #333' : 'none'
        }}
      >
        <div style={{ 
          backgroundColor: audioConfig.primaryColor || '#007bff',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '8px 16px',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '24px' }}>🎵</div>
          <div>
            <h4 style={{ margin: 0, fontSize: '16px' }}>Lecteur Audio</h4>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>
              {audioConfig.playlist?.length || 0} piste{(audioConfig.playlist?.length || 0) > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Contrôles de lecture */}
      <div 
        style={{
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          borderBottom: audioConfig.showPlaylist ? '1px solid #333' : 'none'
        }}
      >
        <button 
          onClick={previousTrack}
          disabled={!audioConfig.playlist || audioConfig.playlist.length === 0}
          title={tr('Piste précédente')}
          style={{
            background: 'transparent',
            border: `2px solid ${audioConfig.primaryColor || '#007bff'}`,
            color: audioConfig.primaryColor || '#007bff',
            borderRadius: '50%',
            width: 'clamp(36px, 6vw, 50px)',
            height: 'clamp(36px, 6vw, 50px)',
            cursor: audioConfig.playlist && audioConfig.playlist.length > 0 ? 'pointer' : 'not-allowed',
            fontSize: 'clamp(12px, 2.5vw, 14px)',
            opacity: audioConfig.playlist && audioConfig.playlist.length > 0 ? 1 : 0.5
          }}
        >
          ⏮
        </button>
        <button 
          onClick={togglePlayPause}
          disabled={!currentTrack}
          title={tr('Lecture / Pause')}
          style={{
            background: audioConfig.primaryColor || '#007bff',
            border: 'none',
            color: 'white',
            borderRadius: '50%',
            width: 'clamp(44px, 7vw, 56px)',
            height: 'clamp(44px, 7vw, 56px)',
            cursor: currentTrack ? 'pointer' : 'not-allowed',
            fontSize: 'clamp(16px, 3vw, 18px)',
            opacity: currentTrack ? 1 : 0.5
          }}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>
        <button 
          onClick={nextTrack}
          disabled={!audioConfig.playlist || audioConfig.playlist.length === 0}
          title={tr('Piste suivante')}
          style={{
            background: 'transparent',
            border: `2px solid ${audioConfig.primaryColor || '#007bff'}`,
            color: audioConfig.primaryColor || '#007bff',
            borderRadius: '50%',
            width: 'clamp(36px, 6vw, 50px)',
            height: 'clamp(36px, 6vw, 50px)',
            cursor: audioConfig.playlist && audioConfig.playlist.length > 0 ? 'pointer' : 'not-allowed',
            fontSize: 'clamp(12px, 2.5vw, 14px)',
            opacity: audioConfig.playlist && audioConfig.playlist.length > 0 ? 1 : 0.5
          }}
        >
          ⏭
        </button>
      </div>

      {/* Affichage de la piste actuelle */}
      {currentTrack && (
        <div 
          style={{
            padding: '12px 16px',
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderBottom: '1px solid #333'
          }}
        >
          <div style={{ fontWeight: '500', fontSize: '14px', marginBottom: '4px' }}>
            🎵 {currentTrack.title}
          </div>
          <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '8px' }}>
            👤 {currentTrack.artist || 'Artiste inconnu'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            <span>{formatTime(currentTime)}</span>
            <div 
              style={{
                flex: 1,
                height: '4px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '2px',
                position: 'relative',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                if (audioRef.current && duration > 0) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const progress = (e.clientX - rect.left) / rect.width;
                  audioRef.current.currentTime = progress * duration;
                }
              }}
            >
              <div 
                style={{
                  width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                  height: '100%',
                  backgroundColor: audioConfig.primaryColor || '#007bff',
                  borderRadius: '2px'
                }}
              />
            </div>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      )}

      {/* Élément audio masqué */}
      {/* Supprimé pour éviter deux <audio> avec la même ref <<< */}

      {/* Forme d'onde (si activée) */}
      {audioConfig.showWaveform && (
        <div 
          style={{
            padding: '16px',
            height: 'min(80px, 20vh)',
            backgroundColor: 'rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div style={{ color: '#666', fontSize: '14px' }}>
            🌊 Visualisation de la forme d'onde
          </div>
        </div>
      )}

      {/* Playlist (si activée) */}
      {audioConfig.showPlaylist && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {audioConfig.playlist && audioConfig.playlist.length > 0 ? (
            audioConfig.playlist.map((track, index) => (
              <div
                key={index}
                onClick={() => selectTrack(index)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  marginBottom: '4px',
                  backgroundColor: index === currentTrackIndex 
                    ? audioConfig.primaryColor || '#007bff'
                    : 'rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: index === currentTrackIndex ? 'none' : '1px solid transparent'
                }}
                onMouseEnter={(e) => {
                  if (index !== currentTrackIndex) {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (index !== currentTrackIndex) {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                  }
                }}
              >
                <div style={{ 
                  fontWeight: index === currentTrackIndex ? '600' : '500', 
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {index === currentTrackIndex && isPlaying ? (
                    <span style={{ fontSize: '12px' }}>🔊</span>
                  ) : (
                    <span style={{ fontSize: '12px' }}>🎵</span>
                  )}
                  {track.title}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.7 }}>
                  👤 {track.artist || 'Artiste inconnu'}
                </div>
              </div>
            ))
          ) : (
            <div 
              style={{
                textAlign: 'center',
                color: '#666',
                padding: '20px',
                fontSize: '14px'
              }}
            >
              <AutoTranslateText sourceLang="fr">Aucune piste dans la playlist</AutoTranslateText>
            </div>
          )}
        </div>
      )}

      {/* Informations sur les fonctionnalités */}
      <div 
        style={{
          padding: '8px 12px',
          fontSize: '11px',
          color: '#666',
          borderTop: '1px solid #333',
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <div>
          {audioConfig.autoplay && '🔁 Auto'} 
          {audioConfig.loop && ' 🔄 Loop'} 
          {audioConfig.shuffle && ' 🔀 Shuffle'}
        </div>
        <div>
          🔊 {Math.round((audioConfig.volume || 0.8) * 100)}%
        </div>
      </div>
    </div>
  );
};

// Définition du plugin
const AudioPlayerPluginModular: PluginInterface = {
  // Métadonnées du plugin
  metadata: {
    id: 'audio-player-advanced',
    name: 'Lecteur Audio/Playlist',
    description: 'Lecteur audio avancé avec visualisation, playlists drag & drop et support multi-formats',
    version: '1.0.0',
    author: 'Book Editor Team',
    category: 'media',
    tags: ['audio', 'musique', 'playlist', 'lecteur', 'visualisation', 'youtube'],
    icon: '🎵'
  },
  
  // ⚠️ Checklist: mettre à jour documentationForAI à chaque évolution fonctionnelle.
  documentationForAI: {
    description: 'Lecteur audio/vidéo avancé avec playlist drag & drop, visualiseur de spectre, thèmes clair/sombre et support MP3/MP4/YouTube/SoundCloud.',
    usage: 'Utilisez ce plugin pour intégrer une playlist musicale, des podcasts, des extraits audio ou vidéo. Chaque piste a un titre, un artiste et une URL de média.',
    configSchema: {
      title: 'string — titre affiché au-dessus du lecteur',
      theme: '"dark" | "light"',
      showPlaylist: 'boolean — afficher la liste des pistes',
      showWaveform: 'boolean — afficher le visualiseur de spectre',
      autoplay: 'boolean',
      loop: 'boolean',
      shuffle: 'boolean',
      playlist: 'Array<{ title: string; artist?: string; url: string; cover?: string }> — tableau de pistes (MP3, MP4, YouTube URL, SoundCloud URL)',
    },
    exampleConfig: {
      title: 'Nos Podcasts',
      theme: 'dark',
      showPlaylist: true,
      showWaveform: true,
      autoplay: false,
      loop: false,
      shuffle: false,
      playlist: [
        { title: 'Épisode 1 – Introduction', artist: 'FlexiHub', url: 'https://example.com/episode1.mp3' },
        { title: 'Épisode 2 – Fonctionnalités', artist: 'FlexiHub', url: 'https://example.com/episode2.mp3' },
      ],
    },
  },

  // Configuration par défaut
  defaultConfig: defaultAudioPlayerConfig,
  
  // Composant de configuration
  ConfigComponent: AudioPlayerConfigComponent,
  
  // Composant de prévisualisation
  PreviewComponent: AudioPlayerRenderer,
  
  // Fonction pour générer le HTML à insérer dans l'éditeur
  generateHTML: (config: AudioPlayerConfig) => {
    const trackCount = config.playlist?.length || 0;
    const descriptionText = trackCount === 0 ? 'Aucune piste' :
                            trackCount === 1 ? '1 piste' :
                            `${trackCount} pistes`;
    
    return generateUniformPlaceholder({
      title: config.title || 'Lecteur Audio',
      description: `🎵 ${descriptionText} dans la playlist`,
      icon: '🎧',
      category: 'media',
      config: config,
      pluginId: 'audio-player-advanced',
      pluginType: 'audio-player-advanced'
    });
  },
};

export default AudioPlayerPluginModular;
